// Macro + lens rollup. The legacy schema has fine-grained categories
// (AI/ML, BioTech, Web3, Security, Extended Reality) and 12 business lines
// (BFSI, NM, Risk, Finance, Legal, HR, Info, Field, Wealth, CX, Strategy, VC).
//
// The product framing is two coarse axes:
//   - Outside (Tech / Industry / Society)  ← rolled up from `categories.name`
//   - Inside  (Client / Field / Enterprise) ← rolled up from `business_lines.key`
//
// We do the rollup client-side for now. Once the schema gets a proper macro
// column on categories and a lens column on business_lines, this file becomes
// a one-liner and the maps below get retired.
//
// Anything not explicitly mapped falls into "Tech" / "Enterprise" by default
// so a missing legacy row doesn't disappear from the cockpit.

import type { BusinessLine, Category } from "./types.ts";

// ---------- Outside axis ----------
export type Macro = "tech" | "industry" | "society";

export const MACRO_LABEL: Record<Macro, string> = {
  tech:     "Tech",
  industry: "Industry",
  society:  "Society",
};

export const MACRO_COLOR: Record<Macro, string> = {
  tech:     "#7b6cff",
  industry: "#56e1d3",
  society:  "#ffc56a",
};

const CATEGORY_TO_MACRO: Record<string, Macro> = {
  "AI/ML":              "tech",
  "Web3":               "tech",
  "Security":           "tech",
  "Extended Reality":   "tech",
  "Compute":            "tech",
  "Quantum":            "tech",
  "BioTech & Life Sciences": "industry",
  "BioTech":            "industry",
  "Life Sciences":      "industry",
  "Climate":            "industry",
  "Energy":             "industry",
  "Manufacturing":      "industry",
  "Mobility":           "industry",
  "Demographics":       "society",
  "Policy":             "society",
  "Labor":              "society",
  "Geopolitics":        "society",
  "Culture":            "society",
};

export function macroOfCategory(cat: Pick<Category, "name" | "macro">): Macro {
  // Prefer DB-stored macro (added in migration 0004); fall back to the static
  // map for any legacy rows that pre-date the backfill.
  if (cat.macro) return cat.macro;
  return CATEGORY_TO_MACRO[cat.name] ?? "tech";
}

// ---------- Inside axis ----------
export type Lens = "client" | "field" | "enterprise";

export const LENS_LABEL: Record<Lens, string> = {
  client:     "Client",
  field:      "Field",
  enterprise: "Enterprise",
};

export const LENS_COLOR: Record<Lens, string> = {
  client:     "#ff7b9c",
  field:      "#9bf0c4",
  enterprise: "#6ba8ff",
};

const BL_TO_LENS: Record<string, Lens> = {
  // Client-facing business
  BFSI:     "client",
  NM:       "client",
  CX:       "client",
  Wealth:   "client",
  // Field / Strategy / Venture
  Field:    "field",
  Strategy: "field",
  VC:       "field",
  // Internal enterprise functions
  Risk:     "enterprise",
  Finance:  "enterprise",
  Legal:    "enterprise",
  HR:       "enterprise",
  Info:     "enterprise",
  IT:       "enterprise",
};

export function lensOfBusinessLine(bl: Pick<BusinessLine, "key" | "lens">): Lens {
  if (bl.lens) return bl.lens;
  return BL_TO_LENS[bl.key] ?? "enterprise";
}

// ---------- Helpers ----------
export const MACROS: Macro[] = ["tech", "industry", "society"];
export const LENSES: Lens[] = ["client", "field", "enterprise"];

/** Stable iteration order for the cockpit grid: outside × inside. */
export function* iterCells(): Generator<{ macro: Macro; lens: Lens }> {
  for (const macro of MACROS) {
    for (const lens of LENSES) {
      yield { macro, lens };
    }
  }
}
